import {AuthState, AuthStore} from "./auth.store";
import {Injectable} from "@angular/core";
import {EdgeService} from "../../data-access/api-gateway/edge.service";
import {catchError, EMPTY, filter, finalize, first, Observable, switchMap, tap} from "rxjs";
import {CustomerService} from "../../data-access/api-customers/customer.service";
import {Customer, CustomerDocument} from "../../data-access/api-customers/Customer";
import {AuthUtil} from "../../shared/utils/auth.util";
import {AuthService} from "@auth0/auth0-angular";

@Injectable({ providedIn: 'root' })
export class EdgeAuthService {
  constructor(
    private store: AuthStore,
    private authService: AuthService,
    private edgeService: EdgeService,
    private customerService: CustomerService,
  ) { }

  public setLoading(isLoading: boolean): void {
    this.store.setLoading(isLoading);
  }

  public refresh(): void {
    this.authService.isLoading$
      .pipe(
        filter((loading: boolean) => !loading),
        switchMap(() => this.authService.isAuthenticated$),
        first(),
        switchMap((isAuth: boolean) => {
          if (!isAuth) {
            this.authService.loginWithRedirect({ appState: { target: '/services'} });
            return EMPTY;
          }
          return this.initAuthValue$();
        }),
        finalize(() => this.setLoading(false)),
        catchError((err) => {
          console.error('[bootstrap edge service] init failed', err);
          return EMPTY;
        })
      ).subscribe();
  }

  private initAuthValue$(): Observable<any> {
    return this.edgeService.me()
      .pipe(
        switchMap((me: any) => {
          const subject = me?.sub ?? me?.subject;
          if (!subject) {
            throw new Error('Missing subject in /me');
          }
          return this.customerService.getMe(subject);
        }),
        tap((user: Customer) => this.authorizedUser(user)),
        catchError(err => {
          console.error(err);
          this.rejectUser(err);
          return EMPTY;
        })
      );
  }

  private authorizedUser(user: Customer): void {
    const authorizedUser: Customer = AuthUtil.Customer(user);
    this.store.update((state: AuthState) => ({
      ...state,
      ...authorizedUser
    }));
  }

  private rejectUser(error?: any): void {
    if (error) {
      this.store.setError(error)
    }
    const rejectedUser: Customer = Customer.ANONYMOUS;
    this.store.update((state: AuthState) => ({
      ...state,
      ...rejectedUser
    }));
  }

  /*
   * UPDATE DATA
   */

  public upsertCustomer(customer: Customer): void {
    this.store.update((state: AuthState) => {
      return ({
        ...state,
        ...customer,
      });
    });
  }

  public updateCustomerDocument(customerDocument: CustomerDocument): void {
    this.store.update((state: AuthState) => {
      let newCustomerDocuments: CustomerDocument[] = state.customerDocuments
        ? [...state.customerDocuments]
        : [];

      const index: number = newCustomerDocuments.findIndex((doc: CustomerDocument) =>
        doc.id === customerDocument.id);

      if (index !== -1) {
        newCustomerDocuments[index] = customerDocument;
      } else {
        newCustomerDocuments.push(customerDocument);
      }

      return {
        ...state,
        customerDocuments: newCustomerDocuments
      };
    });
  }

  public deleteCustomerDocument(documentId: string): void {
    this.store.update((state: AuthState) => {
      let currentCustomerDocuments: CustomerDocument[] = state.customerDocuments
        ? [...state.customerDocuments]
        : [];

      const index: number = currentCustomerDocuments.findIndex((doc: CustomerDocument) =>
        doc.id === documentId);

      if (index !== -1) {
        currentCustomerDocuments.splice(index, 1);
      }

      return {
        ...state,
        customerDocuments: currentCustomerDocuments
      };
    });
  }

}
